import {
  fetchCoAuthorsByPaperId,
  fetchPaperCount,
  fetchPaperFileMetadata,
  fetchPapersByRegistrationId,
} from "../api/conferenceRegistrationApi";

const EDITABLE_PAPER_STATUSES = [
  "DRAFT",
  "MINOR_REVISION",
  "MAJOR_REVISION",
  "REVISION_REQUESTED",
];

const LOCKED_PAPER_STATUSES = [
  "SUBMITTED",
  "UNDER_REVIEW",
  "ACCEPTED",
  "REJECTED",
  "REVIEWED",
];

const normalizePaperStatus = (status) => (status || "").trim().toUpperCase();

const getSubmissionMethodFromPaper = ({ paper, hasFile }) => {
  const hasAbstract =
    paper?.abstractText && paper.abstractText.trim().length > 0;

  if (hasAbstract && hasFile) return "BOTH";
  if (hasAbstract) return "TEXTAREA";
  if (hasFile) return "FILE";
  return "TEXTAREA";
};

const mapCoAuthors = (coAuthorsData = []) =>
  coAuthorsData.map((coAuthor) => ({
    name: coAuthor.name || "",
    email: coAuthor.email || "",
    phone: coAuthor.phone || "",
    specialization: coAuthor.specialization || "",
    designation: coAuthor.designation || "",
  }));

export const loadExistingPaperState = async ({
  registrationBaseUrl,
  existingReg,
  email,
  conferenceName,
}) => {
  let hasPaperSubmitted = false;
  let loadedPaperStatus = null;
  let papers = [];
  let selectedPaper = null;
  let isPaperLocked = false;
  let hasFile = false;
  let fileName = "uploaded-paper.pdf";
  let submissionMethod = "TEXTAREA";
  let coAuthors = [];
  let paperCountPlaceholder = false;

  const savedWantsToSubmitAbstract =
    existingReg.additionalData?.wantsToSubmitAbstract === true ||
    existingReg.additionalData?.wantsToSubmitAbstract === "true";

  const candidateRegistrationIds = [
    existingReg.id,
    existingReg.registrationId,
  ].filter(
    (value, index, self) => value != null && self.indexOf(value) === index
  );

  for (const candidateId of candidateRegistrationIds) {
    const result = await fetchPapersByRegistrationId({
      registrationBaseUrl,
      registrationId: candidateId,
    });

    if (Array.isArray(result) && result.length > 0) {
      papers = result;
      break;
    }
  }

  if (papers.length > 0) {
    hasPaperSubmitted = true;
    selectedPaper =
      papers.find((candidatePaper) =>
        EDITABLE_PAPER_STATUSES.includes(
          normalizePaperStatus(candidatePaper.status)
        )
      ) || papers[0];
    isPaperLocked = LOCKED_PAPER_STATUSES.includes(selectedPaper.status);
    loadedPaperStatus = selectedPaper.status || null;

    const paperRegistrationId =
      selectedPaper.registrationId || existingReg.id || existingReg.registrationId;

    try {
      const fileMetadata = await fetchPaperFileMetadata({
        registrationBaseUrl,
        registrationId: paperRegistrationId,
      });
      hasFile = fileMetadata.exists;
      fileName = fileMetadata.fileName;
    } catch (error) {
      console.log("📎 Error checking file:", error.message);
      hasFile = false;
    }

    submissionMethod = getSubmissionMethodFromPaper({
      paper: selectedPaper,
      hasFile,
    });

    try {
      const coAuthorsData = await fetchCoAuthorsByPaperId({
        registrationBaseUrl,
        paperId: selectedPaper.id,
      });
      if (coAuthorsData && coAuthorsData.length > 0) {
        coAuthors = mapCoAuthors(coAuthorsData);
      }
    } catch (error) {
      console.error("Error loading co-authors:", error);
    }
  }

  if (!hasPaperSubmitted && email && conferenceName) {
    try {
      const parsedCount = await fetchPaperCount({
        registrationBaseUrl,
        email,
        conference: conferenceName,
      });
      if (parsedCount > 0) {
        hasPaperSubmitted = true;
        paperCountPlaceholder = true;
        papers = Array.from({ length: parsedCount }, (_, index) => ({
          id: `count-placeholder-${index}`,
        }));
        if (!loadedPaperStatus) {
          loadedPaperStatus = "DRAFT";
        }
      }
    } catch (fallbackError) {
      console.error("Error in paper count fallback lookup:", fallbackError);
    }
  }

  if (!hasPaperSubmitted && savedWantsToSubmitAbstract) {
    hasPaperSubmitted = true;
    papers = [];
    if (!loadedPaperStatus) {
      loadedPaperStatus = "DRAFT";
    }
  }

  return {
    hasPaperSubmitted,
    loadedPaperStatus,
    papers,
    selectedPaper,
    isPaperLocked,
    hasFile,
    fileName,
    submissionMethod,
    coAuthors,
    paperCountPlaceholder,
    savedWantsToSubmitAbstract,
  };
};
